import React, { useEffect, useState } from 'react';
import { Autocomplete, Button, ButtonGroup, CheckIcon, ClearIcon, DialogActions, DialogContent, Fab, Grid, IconButton, InfoIcon, InputAdornment, Stack, TextField, Typography } from "../../styles/MaterialUI";
import ContentPlaylist from '../ContentPlaylist/ContentPlaylist';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import apiConstants, { INSIGHTS_URL } from '../../constants/apiConstants';
import CustomTooltip from '../StyledComponents/CustomTooltip';
import { resetDashboardState } from '../Redux/Features/addContentSlice';

const EditContentDialog = ({ showAlert, openDashboardMenu, updateContent, closeEditContentModal,  // Pass the current total content count for the org so we can enforce max_contents.
    currentContentCount = 0,
    // Pass the current total storage used (in GB) so we can enforce max_content_storage_limit.
    currentStorageUsedGB = 0 }) => {
    const { is_Error, screen_Data, content_RowData } = useSelector((state) => state.editContent);
    const { ic_content_id, ic_content_name, ic_content_type, ic_content_status, ic_dashboard_view, ic_fit_type, mapped_screens,
        ic_filename, playlist_contents, ic_org_integration_id, ic_mm_dashboard_id, ic_dashboard_url, ic_browser_url, ic_refresh_rate } = content_RowData;
    const { content_Module, content_Workbook, view_WorkbookName, dashboard_List, dashboard_URL_List, breadcrumb_URL } = useSelector((state) => state.addContent);
    const { organizationId, isInternalUserMapped, org_configurations } = useSelector((state) => state.userLogin);
    const { userToken } = useSelector((state) => state.userToken);
    const headers = { Authorization: `Bearer ${userToken}` };
    const [updateContentName, setUpdateContentName] = useState("");
    const [updateContentType, setUpdateContentType] = useState(null);
    const [updateContentStatus, setUpdateContentStatus] = useState(null);
    const [updateContentChooseFit, setUpdateContentChooseFit] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const isWideContent = updateContentType === "IntelliDash Dashboard" || updateContentType === "Content Playlist" || updateContentType === "Machine Metrics" || updateContentType === "Acumatica";
    const [isContentStatusChanged, setIsContentStatusChanged] = useState(false);
    const errorMessages = { contentNameError: "", contentStatusError: "", contentTypeError: "", workbookError: "", dashboardError: "", customViewError: "", dashboardTypeError: "", metricsDashboardUrlError: "", openUrlError: "" };
    const [updateContentErrorMessages, setUpdateContentErrorMessages] = useState(errorMessages);
    const [fileSrc, setFileSrc] = useState(null);
    const [thumbnailName, setThumbnailName] = useState(null);
    const [videoFileName, setVideoFileName] = useState(null);
    const [modifyPlaylistData, setModifyPlaylistData] = useState([]);
    const [contentModule, setContentModule] = useState("");
    const [contentWorkbook, setContentWorkbook] = useState("");
    const [contentDashboard, setContentDashboard] = useState("");
    const [contentView, setContentView] = useState("Original");
    const [customViewList, setCustomViewList] = useState([]);
    const [dashboardList, setDashboardList] = useState([]);
    const [dashboardUrlList, setDashboardUrlList] = useState([]);
    const [viewIdData, setViewIdData] = useState([]);
    const [breadcrumUrl, setBreadcrumUrl] = useState("");
    const [viewWorkbookName, setViewWorkbookName] = useState("");
    const [contentPlaylistData, setContentPlayListData] = useState([]);
    const [enablePreview, setEnablePreview] = useState();
    const [rowData, setRowData] = useState({});
    const [selectedScreen, setSelectedScreen] = useState([]);

    const [metricsDashboardTypeList, setMetricsDashboardTypeList] = useState([]);
    const [metricsDashboardTypeName, setMetricsDashboardTypeName] = useState("");
    const [metricsDashboardTypeId, setMetricsDashboardTypeId] = useState("");
    const [metricsDashboardTypeURL, setMetricsDashboardTypeURL] = useState("");
    const [anchorEl, setAnchorEl] = useState(null);
    const dispatch = useDispatch();
    const [updateContentOpenUrl, setUpdateContentOpenUrl] = useState("");

    const refreshRateOptions = [
        { label: "None", value: "None" },
        { label: "5 Mins", value: "5" },
        { label: "10 Mins", value: "10" },
        { label: "15 Mins", value: "15" },
        { label: "20 Mins", value: "20" },
        { label: "25 Mins", value: "25" },
        { label: "30 Mins", value: "30" },
        { label: "35 Mins", value: "35" },
        { label: "40 Mins", value: "40" },
        { label: "45 Mins", value: "45" },
        { label: "50 Mins", value: "50" },
        { label: "55 Mins", value: "55" },
        { label: "60 Mins", value: "60" },
    ];
    const [updateContentRefreshRate, setUpdateContentRefreshRate] = useState(refreshRateOptions[0]); //None
    const aristocastLimits = org_configurations?.aristocast_limits ?? {};
    const DEFAULT_PLATFORM_VIDEO_LIMIT_MB = 150; // reference value from spec
    const maxContents = aristocastLimits.max_limit_of_aristocast_contents ?? 0;
    const maxContentPerPlaylist = aristocastLimits.max_content_per_playlist ?? 0;
    const videoSizeLimitMB = aristocastLimits.video_content_size_limit?.value ?? 0;
    const maxStorageLimitGB = aristocastLimits.max_content_storage_limit?.value ?? 0;
    const videoSizeLimitUnit = aristocastLimits.video_content_size_limit?.unit ?? 0;
    const maxStorageLimitUnit = aristocastLimits.max_content_storage_limit?.unit ?? 0;

    // Converts a limit value from `unit` into MB.
    const toMB = (value, unit = "MB") => {
        const u = String(unit).toUpperCase();
        if (u === "KB") return value / 1024;
        if (u === "MB") return value;
        if (u === "GB") return value * 1024;
        if (u === "TB") return value * 1024 * 1024;
        return value; // unknown unit — treat as MB
    };

    // Converts a limit value from `unit` into GB.
    const toGB = (value, unit = "GB") => {
        const u = String(unit).toUpperCase();
        if (u === "KB") return value / (1024 * 1024);
        if (u === "MB") return value / 1024;
        if (u === "GB") return value;
        if (u === "TB") return value * 1024;
        return value; // unknown unit — treat as GB
    };

    useEffect(() => {
        if (is_Error) {
            showAlert("error", "Error fetching screen data");
        }
        if (mapped_screens.trim().length > 0) {
            setSelectedScreen(screen_Data);
        }
    }, [screen_Data]);

    useEffect(() => {
        setContentModule(content_Module);
        setContentWorkbook(content_Workbook);
        setViewWorkbookName(view_WorkbookName);
        setDashboardList(dashboard_List);
        setDashboardUrlList(dashboard_URL_List);
        setBreadcrumUrl(breadcrumb_URL);
    }, [content_Module, content_Workbook]);

    const getMetricsDashboardTypes = async () => {
        const apiResponse = await axios.post(`${apiConstants.GET_API_URL}getDashboardTypes`, {}, { headers });
        const { data } = apiResponse.data;
        setMetricsDashboardTypeList(data);
        return data;
    }

    const setContentState = async () => {
        setRowData({ content_id: ic_content_id, mapped_screens: mapped_screens, content_Status: ic_content_status, content_Type: ic_content_type });
        setUpdateContentName(ic_content_name);
        setUpdateContentType(ic_content_type);
        setUpdateContentStatus(ic_content_status);

        if (ic_content_type === "Content Playlist") {
            setModifyPlaylistData(playlist_contents);
            setContentPlayListData(playlist_contents);
        }
        if (ic_content_type === "Image") {
            setUpdateContentChooseFit(ic_fit_type);
            setThumbnailName(ic_filename);
        }
        if (ic_content_type === "Video") {
            setVideoFileName(ic_filename);
        }
        if (ic_content_type === "Machine Metrics") {
            const responseData = await getMetricsDashboardTypes();
            const { ic_mm_dashboard_name } = responseData?.find(data => data.ic_mm_dashboard_id === ic_mm_dashboard_id);
            setMetricsDashboardTypeName(ic_mm_dashboard_name);
            setMetricsDashboardTypeId(ic_mm_dashboard_id);
            setMetricsDashboardTypeURL(ic_dashboard_url);
        }

        if (ic_content_type === "Acumatica") {
            setMetricsDashboardTypeURL(ic_dashboard_url);
            const selectedOption = refreshRateOptions.find(opt => opt.value === (ic_refresh_rate ?? 0)) || refreshRateOptions[0]; // fallback to None

            setUpdateContentRefreshRate(selectedOption);
        }

        if (ic_content_type === "IntelliDash Dashboard") {
            try {
                const part1 = ic_dashboard_view.split("->");
                const lastPart = part1[part1.length - 1].split(": View-");
                const splitData = [...part1.slice(0, -1), ...lastPart];
                const [module_Name, workbook_name, dashboard_name, view_name] = splitData;

                const response = await axios.post(`${INSIGHTS_URL.CAST_API_URL}userDbList`, {}, { headers });
                const { data } = response.data;

                const workbookRecords = data.map((module) => module.SAN_Modules_WBGroups).filter((d) => d[0].module_title === module_Name);

                const selectedWorkbook_Data = workbookRecords[0]
                    .flatMap((record) =>
                        record.SAN_Modules_WBGroup_Workbooks.filter((data) => data.workbook_title === workbook_name)
                    ).map((workbook) => workbook.workbook);

                const dashboard_viewList = selectedWorkbook_Data.flatMap((view) =>
                    view.SAN_TableauViews.sort((a, b) => a.wb_view_order - b.wb_view_order).map((d) => d.view_title));

                const dashboardDatas = selectedWorkbook_Data.flatMap((view) =>
                    view.SAN_TableauViews.map((datas) => ({ view_name: datas.view_title, view_portal_url: datas.view_portal_url, }))
                );

                const viewWorkbook = selectedWorkbook_Data[0].view_workbook;
                const Viewresponse = await axios.post(`${INSIGHTS_URL.CAST_API_URL}usercustomviewList`,
                    { view_workbook: viewWorkbook, dashboard_name: dashboard_name }, { headers });

                const viewResp = Viewresponse.data.data;
                const responseData = viewResp.map((view) => view.cust_view_name);
                responseData.unshift("Original");
                setCustomViewList(responseData);
                setViewIdData(viewResp);
                setContentView(view_name);
                setContentModule(module_Name);
                setContentWorkbook(workbook_name);
                setViewWorkbookName(viewWorkbook);
                setContentDashboard(dashboard_name);
                setDashboardList(dashboard_viewList);
                setDashboardUrlList(dashboardDatas);
                setBreadcrumUrl(ic_dashboard_view);
            } catch (error) {
                const { statusCode, message, redirect_url } = error.response.data;
                showAlert("error", message);
                if (statusCode === 403) {
                    window.open(redirect_url, "_self");
                }
            }
        }

        if (ic_content_type === "Open URL") {
            setUpdateContentOpenUrl(ic_browser_url);
            const selectedOption = refreshRateOptions.find(opt => opt.value === (ic_refresh_rate ?? 0)) || refreshRateOptions[0]; // fallback to None

            setUpdateContentRefreshRate(selectedOption);
        }
    }

    useEffect(() => {
        setContentState();
    }, []);

    const validateImageFormat = (event) => {
        const fileInput = event.target;

        if (fileInput.files.length > 0) {
            const selectedFile = fileInput.files[0];
            const allowedFormats = ["image/jpeg", "image/jpg", "image/png"];

            if (allowedFormats.includes(selectedFile.type)) {
                const fileSizeInMB = selectedFile.size / (1024 * 1024);
                if (fileSizeInMB > 50) {
                    showAlert("error", "File size exceeds 50MB. Please upload a smaller image");
                    fileInput.value = "";
                    setThumbnailName(null);
                    return;
                }
                setFileSrc(selectedFile);
                setThumbnailName(selectedFile.name);
            } else {
                showAlert("error", "Invalid file format. Please select an image file");
                fileInput.value = "";
                setThumbnailName(null);
            }
        } else {
            setThumbnailName(null);
        }
    };

    // ── Max-storage guard ────────────────────────────────────────────────────
    /**
     * Call this before saving content that involves a file upload.
     * fileSizeBytes — the size of the file the user is about to upload.
     * Returns true if the upload would not exceed the storage limit.
     */
    const checkStorageLimit = (fileSizeBytes = 0) => {
        return true;
        const fileSizeGB = fileSizeBytes / (1024 * 1024 * 1024);
        const maxGB = toGB(maxStorageLimitGB, maxStorageLimitUnit);
        if (currentStorageUsedGB + fileSizeGB > maxGB) {
            showAlert(
                "error",
                "Maximum Storage Limit reached. Delete existing Contents to free up space or upgrade to expand storage space."
            );
            return false;
        }
        return true;
    };



    const validateVideoSizeLimit = (fileSizeInMB) => {
        // Convert the configured limit to MB regardless of what unit it was stored in.
        const configuredLimitMB = videoSizeLimitMB
            ? toMB(videoSizeLimitMB, videoSizeLimitUnit)
            : DEFAULT_PLATFORM_VIDEO_LIMIT_MB;
        const effectiveLimit = DEFAULT_PLATFORM_VIDEO_LIMIT_MB;

        if (fileSizeInMB > configuredLimitMB) {
            if (configuredLimitMB && configuredLimitMB < DEFAULT_PLATFORM_VIDEO_LIMIT_MB) {
                showAlert(
                    "error",
                    "The uploaded video exceeds the maximum allowed video size limit. Upgrade to upload larger Videos."
                );
            } else {
                showAlert("error", `Cannot upload videos larger than ${effectiveLimit} MB`);
            }
            return false;
        }
        return true;
    };


    const validateVideoFormat = (event) => {
        const fileInput = event.target;

        if (fileInput.files.length > 0) {
            const selectedFile = fileInput.files[0];
            const allowedFormats = ["video/mp4", "video/quicktime", "video/x-ms-wmv", "video/avi", "video/x-msvideo"];

            if (allowedFormats.includes(selectedFile.type)) {
                const fileSizeInMB = selectedFile.size / (1024 * 1024);
                // ── NEW: per-org video size limit check ──────────────────
                if (!validateVideoSizeLimit(fileSizeInMB)) {
                    fileInput.value = "";
                    setVideoFileName(null);
                    return;
                }

                // ── NEW: storage limit check ─────────────────────────────
                if (!checkStorageLimit(selectedFile.size)) {
                    fileInput.value = "";
                    setVideoFileName(null);
                    return;
                }

                setFileSrc(selectedFile);
                setVideoFileName(selectedFile.name);
            } else {
                showAlert("error", "Invalid file format. Please select a video file");
                fileInput.value = "";
                setVideoFileName(null);
            }
        } else {
            setVideoFileName(null);
        }
    };

    const generateDashboardUrl = (dashboard_UrlList, viewType, viewList = []) => {
        const filteredData = dashboard_UrlList.find((data) => data.view_name === contentDashboard);
        if (!filteredData) {
            return "";
        }
        const [, workbookName, dashboardName] = filteredData.view_portal_url.split("~");
        const baseURL = `https://intellitab.intellidash.net/#/site/intelli/views/${workbookName}/${dashboardName}`;

        if (viewType === "Original") {
            return baseURL;
        }

        const view = viewList.find((view) => view.cust_view_name === viewType);
        return view ? `${baseURL}/${view.cv_luid}/${view.cv_url_id}` : false;
    };

    const validateContentPlaylist = (playlistData) => {
        if (!playlistData.length) {
            return { isError: true, errorMessage: "Please include at least one content in the playlist." };
        }
        const content = new Set();

        for (const item of playlistData) {
            const { cType, cName, cSettings, cStatus } = item;

            if (!cType || !cName || !cSettings || !cStatus) {
                return { isError: true, errorMessage: "All fields in the playlist must be filled in." };
            }

            const uniqueKey = `${cType}-${cName}`;

            if (content.has(uniqueKey)) {
                return { isError: true, errorMessage: `An entry with content type: ${cType} and content came: ${cName} already exists.` };
            }

            content.add(uniqueKey);
        }

        return { isError: false, errorMessage: "" };
    }

    const handleAddContentPlaylist = (contentPlaylist) => {
        const settingsMap = { "Play Once": 1, "Play Twice": 2, "Play Thrice": 3 };
        const updatedContent = contentPlaylist.map((content, index) =>
        ({
            ...content, cSettingsType: content.cType === "video" ? "loop" : "mins", cOrder: index + 1,
            cSettingsValue: content.cType === "video" ? settingsMap[content.cSettings] || content.cSettings : content.cSettings
        }));

        setContentPlayListData(updatedContent);
    };

    const contentPlaylistError = (errorMessage) => {
        showAlert("error", errorMessage);
    }

    const handleSelectWorkbook = () => {
        setEnablePreview(false);
        setUpdateContentErrorMessages({ workbookError: "" });
        setContentModule("");
        setContentWorkbook("");
        setContentDashboard("");
        setViewWorkbookName("");
        setDashboardList([]);
        setDashboardUrlList([]);
        setViewIdData([]);
        setContentView("Original");
        setBreadcrumUrl("");
        openDashboardMenu();
        dispatch(resetDashboardState());
    };

    const handleDashboardChange = async (newValue) => {
        setEnablePreview(false);
        try {
            const newBreadcrumUrl = newValue ? `${contentModule}->${contentWorkbook}->${newValue}:${" "}${contentView ? `View-${contentView}`
                : ""}` : `${contentModule}->${contentWorkbook}`;

            setBreadcrumUrl(newBreadcrumUrl);
            setUpdateContentErrorMessages({ dashboardError: newValue ? "" : updateContentErrorMessages.dashboardError });
            setContentDashboard(newValue);

            if (newValue) {
                const response = await axios.post(`${INSIGHTS_URL.CAST_API_URL}usercustomviewList`,
                    { view_workbook: viewWorkbookName, dashboard_name: newValue }, { headers });

                const { data } = response.data;
                const responseData = data.map((view) => view.cust_view_name);
                responseData.unshift("Original");
                setCustomViewList(responseData);
                setViewIdData(data);
            } else {
                setCustomViewList(["Original"]);
                setViewIdData([]);
            }
        } catch (error) {
            const { statusCode, message, redirect_url } = error.response.data;
            showAlert("error", message);
            if (statusCode === 403) {
                window.open(redirect_url, "_self");
            }
        }
    };

    const handleUpdateContent = async (e) => {
        e.preventDefault();

        if (!updateContentName.trim()) {
            setErrorMessage("Content name cannot be empty");
            return;
        }
        const error = {};
        if (!updateContentStatus) {
            error.contentStatusError = "Please select content status";
        } else if (!updateContentType) {
            error.contentTypeError = "Please select content type";
        }
        if (updateContentType === "IntelliDash Dashboard") {
            if (!contentWorkbook) {
                error.workbookError = "Workbook cannot be empty";
            } else if (!contentDashboard) {
                error.dashboardError = "Please select dashboard";
            } else if (!contentView) {
                error.customViewError = "Please select view";
            }
        }
        if (updateContentType === "Machine Metrics") {
            if (!metricsDashboardTypeName) {
                error.dashboardTypeError = "Please select dashboard type";
            } else if (!metricsDashboardTypeURL) {
                error.metricsDashboardUrlError = "Please enter dashboard url";
            }
        }


        if (updateContentType === "Acumatica") {
            if (!metricsDashboardTypeURL) {
                error.metricsDashboardUrlError = "Please enter dashboard url";
            }
        }


        if (updateContentType === "Open URL") {
            if (!updateContentOpenUrl.trim()) {
                error.openUrlError = "URL cannot be empty";
            }
        }

        if (Object.keys(error).length > 0) {
            setUpdateContentErrorMessages(error);
            return;
        }

        const contentTypeMap = {
            "IntelliDash Dashboard": "dashboard", "Content Playlist": "content_playlist", "Machine Metrics": "orchestrated_url",
            "Open URL": "open_url", "Acumatica": "orchestrated_url"
        };
        const status = updateContentStatus === "Enabled" ? 1 : 0;
        const contentType = contentTypeMap[updateContentType] || updateContentType.toLowerCase();
        const formData = new FormData();
        formData.append("content_id", rowData.content_id);
        formData.append("org_id", organizationId);
        formData.append("content_name", updateContentName.trim());
        formData.append("content_type", contentType);
        formData.append("content_status", status);

        if (updateContentType === "IntelliDash Dashboard") {
            const tableauUrl = generateDashboardUrl(dashboardUrlList, contentView, viewIdData);
            if (!tableauUrl) {
                showAlert("error", "The custom view of the workbook is no longer available in IntelliDash Web Portal.");
                return;
            }
            formData.append("dashboard_url", tableauUrl);
            formData.append("dashboard_view", breadcrumUrl);
            formData.append("view_type", contentView);
        }

        if (updateContentType === "Machine Metrics") {
            formData.append("dashboard_url", metricsDashboardTypeURL.trim());
            formData.append("org_integration_id", ic_org_integration_id);
            formData.append("mm_dashboard_id", metricsDashboardTypeId);
            formData.append("dashboard_view", "Original");
        }

        if (updateContentType === "Acumatica") {
            formData.append("dashboard_url", metricsDashboardTypeURL.trim());
            formData.append("org_integration_id", ic_org_integration_id);
            formData.append("dashboard_view", "Original");
            formData.append("refresh_rate", updateContentRefreshRate.value);
        }

        if (updateContentType === "Open URL") {
            formData.append("browser_url", updateContentOpenUrl.trim());
            formData.append("refresh_rate", updateContentRefreshRate.value);
        }

        if (updateContentType === "Image") {
            if (fileSrc) {
                formData.append("file", fileSrc);
            }

            if (!updateContentChooseFit) {
                showAlert("error", "Please select image fit");
                return;
            }
            formData.append("fit_type", updateContentChooseFit);

            if (!thumbnailName) {
                showAlert("error", "Please select an image");
                return;
            }
        }

        if (updateContentType === "Video") {
            if (fileSrc) {
                formData.append("file", fileSrc);
            }

            if (!videoFileName) {
                showAlert("error", "Please select a video");
                return;
            }
        }

        if (updateContentType === "Content Playlist") {
            const { isError, errorMessage } = validateContentPlaylist(contentPlaylistData);
            if (isError) {
                showAlert("error", errorMessage);
                return;
            }

            const contentData = contentPlaylistData.map(content =>
                ({ content_id: content.content_Id, display_setting: content.cSettingsValue, display_setting_type: content.cSettingsType, content_sequence: content.cOrder }));

            formData.append("playlist_contents", JSON.stringify(contentData));
        }
        const inputContent = { content_Type: updateContentType, isContentStatusChanged, content_Ids: [rowData.content_id], row_Data: rowData, mapped_Screens: selectedScreen, uploadFilename: thumbnailName || videoFileName };
        updateContent(formData, inputContent);
    };

    const handleDashboardTypeChange = (newValue) => {
        const { ic_mm_dashboard_id, ic_mm_dashboard_name } = newValue || {};
        setMetricsDashboardTypeId(ic_mm_dashboard_id);
        setMetricsDashboardTypeName(ic_mm_dashboard_name);
        if (newValue) {
            setUpdateContentErrorMessages({ dashboardTypeError: "" });
        }
        setMetricsDashboardTypeURL("");
    };

    const handleDashboardURLChange = (event) => {
        const { value } = event.target;
        setMetricsDashboardTypeURL(value);
        if (value.trim()) {
            setUpdateContentErrorMessages({ metricsDashboardUrlError: "" });
        }
    };

    const handleTooltipOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleTooltipClose = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);
    const tooltipContent = `
    <span style="font-size: 11px; line-height: 0;">This field should hold the MachineMetrics URL of the dashboard for which you want the AristoCAST content to be created.</span><br/>
    <u><strong style="font-size: 11px;">Steps:</strong></u><br/>
    <span style="font-size: 11px;">1) Login to your MachineMetrics account and navigate to the dashboard of interest.</span><br/>
    <span style="font-size: 11px;">2) Select the necessary Group, View and other parameters on the dashboard.</span><br/>
    <span style="font-size: 11px;">3) Once the dashboard is refreshed, copy the MachineMetrics URL and paste it in this box.</span>
  `;
    const tooltipContentAcumatica = ` <span style="font-size: 11px;">This field should hold the URL of the "Acumatica" form that you wish to display on the AristoCAST screen. Ex: https://acumatica-demo.intellidash.net/AcumaticaERP/Main?ScreenId=AM0034DB</span><br/>`


    // Phase-3 release
    // const handleContentPreview = async (id) => {
    //     try {
    //         const response = await axios.post(`${apiConstants.GET_API_URL}getPreviewimage`, { content_id: id }, { headers });
    //         const { data } = response;

    //         setPreviewModal({ openPreview: true, contentType: updateContentType, chooseFit: updateContentChooseFit, previewData: data.preview_image });
    //     } catch (error) {
    //         const { statusCode, message, redirect_url } = error.response.data;
    //         setToastInfo({ open: true, severity: "error", message: message });
    //         if (statusCode === 403) {
    //             window.open(redirect_url, "_self");
    //         }
    //     }
    // };

    return (
        <>
            <form method="post" encType="multipart/form-data">
                <DialogContent dividers>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={12} md={isWideContent ? 6 : 4} lg={isWideContent ? 6 : 4} xl={isWideContent ? 6 : 4}>
                            <TextField variant="outlined" label="Content Name" size="small" required value={updateContentName} autoComplete="off" sx={{ width: "100%" }}
                                onChange={(e) => {
                                    const { value } = e.target;
                                    setUpdateContentName(value);
                                    if (value) {
                                        setErrorMessage("");
                                    }
                                }}
                                error={!!errorMessage} helperText={errorMessage}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            {!errorMessage && updateContentName.trim().length > 0 ? (
                                                <CheckIcon color="success" sx={{ fontSize: "1.2rem" }} />
                                            ) : ("")}
                                            {errorMessage ? <ClearIcon color="error" sx={{ fontSize: "1rem" }} /> : ""}
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={12} md={isWideContent ? 6 : 4} lg={isWideContent ? 6 : 4} xl={isWideContent ? 6 : 4}>
                            <Autocomplete
                                size="small" options={["Enabled", "Disabled"]} value={updateContentStatus}
                                onChange={(e, value) => {
                                    setUpdateContentStatus(value);
                                    setIsContentStatusChanged((prev) => !prev);
                                    if (value) {
                                        setUpdateContentErrorMessages({ contentStatusError: "" });
                                    }
                                }}
                                renderInput={(params) => (
                                    <TextField {...params} variant="outlined" label="Content Status" error={!!updateContentErrorMessages.contentStatusError}
                                        helperText={updateContentErrorMessages.contentStatusError} />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} sm={12} md={isWideContent ? 6 : 4} lg={isWideContent ? 6 : 4} xl={isWideContent ? 6 : 4}>
                            <Autocomplete size="small" disabled options={["IntelliDash Dashboard", "Image", "Video", "Content Playlist", "Machine Metrics", "Open URL", "Acumatica"]} value={updateContentType}
                                renderInput={(params) => (
                                    <TextField {...params} variant="outlined" label="Content Type" error={!!updateContentErrorMessages.contentTypeError}
                                        helperText={updateContentErrorMessages.contentTypeError} />
                                )}
                            />
                        </Grid>
                        {updateContentType === "Image" &&
                            <>
                                <Grid container item xs={12} display="flex" justifyContent="space-between" >
                                    <Stack>
                                        <Typography>Upload an image</Typography>
                                        <input className="upload-thumbnail1" type="file" name="file" accept="image/*" onChange={validateImageFormat} hidden />
                                        <Stack direction="row" gap={1} display="flex" justifyContent="flex-start" alignItems="center">
                                            <Fab size="small" variant="extended" onClick={() => document.querySelector(".upload-thumbnail1").click()}
                                                sx={{
                                                    width: "50%", textTransform: "none", backgroundColor: "#003366", color: "#fff", boxShadow: "none",
                                                    "&:hover": {
                                                        backgroundColor: "#003366"
                                                    }
                                                }}
                                            >Choose File</Fab>
                                            <span className="thumbnail-name" style={{ fontSize: "12px", display: "inline-block", maxWidth: "150px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{thumbnailName ? thumbnailName : "No file chosen"}</span>
                                        </Stack>
                                        <p style={{ fontSize: "0.8rem", marginTop: 0 }}>
                                            Image size should not exceed 50 MB.
                                        </p>
                                    </Stack>
                                    <Stack>
                                        <Typography>Choose a fit</Typography>
                                        <ButtonGroup variant="contained" aria-label="buttonGroup">
                                            <Button size="small" onClick={() => setUpdateContentChooseFit("Fill")}
                                                style={{
                                                    textTransform: "none", backgroundColor: updateContentChooseFit === "Fill" ? "#ff8000" : "#003366",
                                                }}
                                            >
                                                Fill
                                            </Button>
                                            <Button size="small" onClick={() => setUpdateContentChooseFit("Fit")}
                                                style={{
                                                    textTransform: "none", backgroundColor: updateContentChooseFit === "Fit" ? "#ff8000" : "#003366",
                                                }}
                                            >
                                                Fit
                                            </Button>
                                            <Button size="small" onClick={() => setUpdateContentChooseFit("Stretch")}
                                                style={{
                                                    textTransform: "none", backgroundColor: updateContentChooseFit === "Stretch" ? "#ff8000" : "#003366",
                                                }}
                                            >
                                                Stretch
                                            </Button>
                                            <Button size="small" onClick={() => setUpdateContentChooseFit("Original")}
                                                style={{
                                                    textTransform: "none", backgroundColor: updateContentChooseFit === "Original" ? "#ff8000" : "#003366",
                                                }}
                                            >
                                                Original
                                            </Button>
                                        </ButtonGroup>
                                    </Stack>
                                </Grid>
                            </>}

                        {updateContentType === "Content Playlist" && <ContentPlaylist addPlaylist={handleAddContentPlaylist} showError={contentPlaylistError}
                            modifyPlaylist={modifyPlaylistData} maxContentPerPlaylist={maxContentPerPlaylist} />}

                        {updateContentType === "Video" &&
                            <>
                                <Grid container item xs={12} display="flex" justifyContent="space-between">
                                    <Stack>
                                        <Typography>Upload a video</Typography>
                                        <input className="upload-video" type="file" name="file" accept="video/mp4,video/avi, video/quicktime, video/x-ms-wmv, video/x-msvideo" onChange={validateVideoFormat} hidden />
                                        <Stack direction="row" gap={1} display="flex" justifyContent="flex-start" alignItems="center">
                                            <Fab size="small" variant="extended" onClick={() => document.querySelector(".upload-video").click()}
                                                sx={{
                                                    width: "50%", textTransform: "none", backgroundColor: "#003366", color: "#fff", boxShadow: "none",
                                                    "&:hover": {
                                                        backgroundColor: "#003366"
                                                    }
                                                }}
                                            >Choose File</Fab>
                                            <span className="thumbnail-name" style={{ fontSize: "12px", display: "inline-block", maxWidth: "150px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{videoFileName ? videoFileName : "No file chosen"}</span>
                                        </Stack>
                                        <p style={{ fontSize: "0.8rem", marginTop: 0 }}>
                                            Video size should not exceed 150 MB.
                                        </p>
                                    </Stack>
                                </Grid>
                            </>}

                        {updateContentType === "IntelliDash Dashboard" &&
                            <>
                                <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                                    <TextField variant="outlined" size="small" value={contentWorkbook} label="Workbook Name" required disabled sx={{ width: "100%" }}
                                        error={!!updateContentErrorMessages.workbookError} helperText={updateContentErrorMessages.workbookError}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <Button variant="outlined" size="small" color="primary" onClick={handleSelectWorkbook}
                                                        sx={{ textTransform: "none", "&:hover": { bgcolor: "#003366", color: "#fff", }, left: "10px", }}
                                                    >
                                                        Select Workbook
                                                    </Button>
                                                </InputAdornment>
                                            ),
                                        }}
                                        InputLabelProps={{ shrink: contentWorkbook ? true : false }}
                                    />
                                </Grid>
                                {contentWorkbook &&
                                    <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                                        <Autocomplete size="small" options={dashboardList} value={contentDashboard} onChange={(e, value) => handleDashboardChange(value)}
                                            renderInput={(params) => (
                                                <TextField {...params} variant="outlined" label="Select Dashboard" required
                                                    error={!!updateContentErrorMessages.dashboardError} helperText={updateContentErrorMessages.dashboardError} />
                                            )}
                                        />
                                    </Grid>}

                                {contentDashboard &&
                                    <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                                        <Autocomplete size="small" options={customViewList} value={contentView}
                                            onChange={(e, value) => {
                                                const newBreadcrumUrl = value
                                                    ? `${contentModule}->${contentWorkbook}->${contentDashboard}:${" "}View-${value}`
                                                    : `${contentModule}->${contentWorkbook}->${contentDashboard}`;

                                                setBreadcrumUrl(newBreadcrumUrl);
                                                setUpdateContentErrorMessages({
                                                    customViewError: value ? "" : updateContentErrorMessages.customViewError,
                                                });
                                                setContentView(value);
                                                setEnablePreview(false);
                                            }}
                                            renderInput={(params) => (
                                                <TextField {...params} variant="outlined" label="Select View" required error={!!updateContentErrorMessages.customViewError}
                                                    helperText={updateContentErrorMessages.customViewError} />
                                            )}
                                        />
                                    </Grid>}
                                {contentWorkbook &&
                                    <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                                        <a href="#" style={{ color: "#003366" }}> {breadcrumUrl} </a>
                                    </Grid>}
                            </>}

                        {updateContentType === "Open URL" &&
                            <>
                                <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                                    <TextField variant="outlined" label="URL" size="small" required value={updateContentOpenUrl} autoComplete="off" sx={{ width: "100%" }}
                                        onChange={(e) => {
                                            const { value } = e.target;
                                            setUpdateContentOpenUrl(value);
                                            if (value) {
                                                setUpdateContentErrorMessages({ openUrlError: "" });
                                            }
                                        }}
                                        error={!!updateContentErrorMessages.openUrlError} helperText={updateContentErrorMessages.openUrlError}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                                    <Autocomplete size="small" options={refreshRateOptions} value={updateContentRefreshRate}
                                        getOptionLabel={(option) => option.label}
                                        isOptionEqualToValue={(opt, val) => opt.value === val.value}
                                        onChange={(e, value) => {
                                            setUpdateContentRefreshRate(value ?? refreshRateOptions[0]);
                                        }}
                                        renderInput={(params) => (<TextField {...params} variant="outlined" label="Refresh Rate" />)}
                                        ListboxProps={{ sx: { scrollbarWidth: "thin" } }}
                                    />
                                </Grid>
                            </>
                        }

                        {updateContentType === "Machine Metrics" &&
                            <>
                                <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                                    <Autocomplete size="small" options={metricsDashboardTypeList} getOptionLabel={(option) => option?.ic_mm_dashboard_name}
                                        value={metricsDashboardTypeList.find((option) => option.ic_mm_dashboard_name === metricsDashboardTypeName) || null} onChange={(_, value) => handleDashboardTypeChange(value)}
                                        renderInput={(params) => (
                                            <TextField {...params} variant="outlined" label="Dashboard Type" required error={!!updateContentErrorMessages.dashboardTypeError}
                                                helperText={updateContentErrorMessages.dashboardTypeError} />
                                        )}
                                    />
                                </Grid>
                                {metricsDashboardTypeName &&
                                    <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                                        <TextField variant="outlined" label="Machine Metrics Dashboard URL" size="small" required value={metricsDashboardTypeURL} sx={{ width: "100%" }} onChange={(e) => handleDashboardURLChange(e)}
                                            error={!!updateContentErrorMessages.metricsDashboardUrlError} helperText={updateContentErrorMessages.metricsDashboardUrlError} autoComplete="off"
                                            InputProps={{
                                                endAdornment: (
                                                    <>
                                                        <IconButton edge="end" aria-label="dashboardUrlInfo" onMouseEnter={handleTooltipOpen} onMouseLeave={handleTooltipClose} onClick={handleTooltipOpen}>
                                                            <InfoIcon color="info" />
                                                        </IconButton>
                                                        <CustomTooltip anchorEl={anchorEl} open={open} handleClose={handleTooltipClose} content={tooltipContent} />
                                                    </>
                                                ),
                                            }}
                                        />
                                    </Grid>}
                            </>}

                        {updateContentType === "Acumatica" &&
                            <>


                                <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                                    <TextField variant="outlined" label="Acumatica Dashboard URL" size="small" required value={metricsDashboardTypeURL} sx={{ width: "100%" }} onChange={(e) => handleDashboardURLChange(e)}
                                        error={!!updateContentErrorMessages.metricsDashboardUrlError} helperText={updateContentErrorMessages.metricsDashboardUrlError} autoComplete="off"
                                        InputProps={{
                                            endAdornment: (
                                                <>
                                                    <IconButton edge="end" aria-label="dashboardUrlInfo" onMouseEnter={handleTooltipOpen} onMouseLeave={handleTooltipClose} onClick={handleTooltipOpen}>
                                                        <InfoIcon color="info" />
                                                    </IconButton>
                                                    <CustomTooltip anchorEl={anchorEl} open={open} handleClose={handleTooltipClose} content={tooltipContentAcumatica} />
                                                </>
                                            ),
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                                    <Autocomplete size="small" options={refreshRateOptions} value={updateContentRefreshRate}
                                        getOptionLabel={(option) => option.label}
                                        isOptionEqualToValue={(opt, val) => opt.value === val.value}
                                        onChange={(e, value) => {
                                            setUpdateContentRefreshRate(value ?? refreshRateOptions[0]);
                                        }}
                                        renderInput={(params) => (<TextField {...params} variant="outlined" label="Refresh Rate" />)}
                                        ListboxProps={{ sx: { scrollbarWidth: "thin" } }}
                                    />
                                </Grid>
                            </>}
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ display: "flex", justifyContent: "flex-end" }}>
                    {/* Required for phase-3 release */}
                    {/* <Button variant="contained" size="small" color="info" disabled={!enablePreview} onClick={() => handleContentPreview(rowData.content_id)}
                        sx={{
                            textTransform: "none",
                            "&:hover": {
                                bgcolor: "#026faa",
                            },
                            marginLeft: "20px",
                        }}
                    >
                        Preview
                    </Button> */}
                    <Stack direction="row" spacing={1} sx={{ marginRight: "20px" }}>
                        <Button type="submit" variant="contained" size="small" onClick={handleUpdateContent} disabled={isInternalUserMapped === 1}
                            sx={{ textTransform: "none", bgcolor: "#003366", "&:hover": { bgcolor: "#0d45a5", }, }}
                        >
                            Update
                        </Button>
                        <Button variant="contained" size="small" color="error" onClick={closeEditContentModal} sx={{ textTransform: "none" }}>
                            Cancel
                        </Button>
                    </Stack>
                </DialogActions>
            </form>
        </>
    )
}

export default EditContentDialog;
